import HoverToggle from '@/shared/components/HoverToggle';
import PropTypes from 'prop-types';
import CompoundDaysTimeInput from '@/shared/components/input/CompoundDaysTimeInput';
import React, { useMemo, useCallback, useState } from 'react';
import { Dropdown, MenuItem, OverlayTrigger, Popover } from 'react-bootstrap';
import { CanDelete, CanRead } from '@/shared/components/security';
import AuthService from '@/shared/services/AuthService';
import intlShape from '@/shared/utils/intlShape';
import { displayDaysAndTime, getTimeAbbr } from '@/shared/utils/absenceFormat';
import messages from '../hrmMessages';

const AbsenceCounterCell = ({
  counter,
  resourceId,
  employeeId,
  userId,
  intl,
  onInitCounter,
  onDeleteCounter,
  onOpenRegistrationOverview,
  onOpenCounterDetails,
  inDays,
}) => {
  const currentUser = useMemo(() => AuthService.getUserId(), []);
  const [initDays, setInitDays] = useState(0);
  const [initMinutes, setInitMinutes] = useState(0);
  const [saving, setSaving] = useState(false);

  const isEmpty = (initDays ?? 0) === 0 && (initMinutes ?? 0) === 0;

  const handleSave = useCallback(() => {
    if (isEmpty) return;
    setSaving(true);
    onInitCounter({
      resourceId,
      employeeId,
      startValueWholeDays: initDays,
      startValueRemainderMinutes: initMinutes,
    })
      .then(() => {
        setInitDays(0);
        setInitMinutes(0);
        document.body.click(); // close popover
      })
      .finally(() => setSaving(false));
  }, [initDays, initMinutes, resourceId, employeeId, onInitCounter, isEmpty]);

  if (counter?.id) {
    const counterInfo = {
      ...counter,
      employeeId,
      resourceId,
    };
    const isCurrentUser = userId === currentUser;

    const abbr = getTimeAbbr(intl, messages);
    const balanceDisplay = inDays
      ? displayDaysAndTime(counter.balanceWholeDays, counter.balanceRemainderMinutes, abbr)
      : (counter.balance ?? 0).toTime();
    const startDisplay = inDays
      ? displayDaysAndTime(counter.startValueWholeDays, counter.startValueRemainderMinutes, abbr)
      : (counter.startValue ?? 0).toTime();

    return (
      <Dropdown id={`Dropdown-${employeeId}-${resourceId}`}>
        <span className="info">
          <a
            title={intl.formatMessage(messages.CounterBalanceStartValue, {
              balanceDays: displayDaysAndTime(counter.balanceWholeDays, counter.balanceRemainderMinutes, abbr),
              balanceHours: (counter.balance ?? 0).toTime(),
              startValueHours: (counter.startValue ?? 0).toTime(),
              startValueDays: displayDaysAndTime(counter.startValueWholeDays, counter.startValueRemainderMinutes, abbr),
            })}
            onClick={() => onOpenCounterDetails(counterInfo)}
          >
            {balanceDisplay}/{startDisplay}
          </a>
        </span>
        <HoverToggle bsRole="toggle" />

        <Dropdown.Menu className="unselectable">
          <CanRead on={isCurrentUser ? 'RegistrationOwn' : 'RegistrationOthers'}>
            <MenuItem
              eventKey="0"
              onSelect={() => onOpenRegistrationOverview({ userId, resourceId })}
            >
              <i className="fa fa-th fa-fw margin5R" />
              {intl.formatMessage(messages.OpenRegistrationOverview)}
            </MenuItem>
          </CanRead>
          <MenuItem eventKey="0" onSelect={() => onOpenCounterDetails(counterInfo)}>
            <i className="fa fa-search fa-fw margin5R" />
            {intl.formatMessage(messages.OpenDetails)}
          </MenuItem>
          <CanDelete on="AbsenceAndCounters">
            <MenuItem eventKey="1" onSelect={() => onDeleteCounter(counterInfo)}>
              <i className="fa fa-trash fa-fw margin5R" />
              {intl.formatMessage(messages.Delete)}
            </MenuItem>
          </CanDelete>
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  const popover = (
    <Popover
      id={`init-popover-${employeeId}-${resourceId}`}
      title={intl.formatMessage(messages.InitializeCounter)}
      style={{ minWidth: '320px' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', padding: '4px 0' }}>
        <CompoundDaysTimeInput
          id={`init-${employeeId}-${resourceId}`}
          wholeDays={initDays}
          remainderMinutes={initMinutes}
          disabled={saving}
          showLabels
          daysLabel={intl.formatMessage(messages.Days)}
          hoursLabel={intl.formatMessage(messages.Hours)}
          onChange={({ wholeDays, remainderMinutes }) => {
            setInitDays(wholeDays);
            setInitMinutes(remainderMinutes);
          }}
        />
        <button
          type="button"
          className="btn btn-sm btn-inverse"
          onClick={handleSave}
          disabled={isEmpty || saving}
        >
          {saving ? <i className="fa fa-spinner fa-spin margin5R" /> : <i className="fa fa-check margin5R" />}
          {intl.formatMessage(messages.Save)}
        </button>
      </div>
    </Popover>
  );

  return (
    <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={popover}>
      <a className="clickable" title={intl.formatMessage(messages.InitializeCounter)}>
        <i className="fa fa-fw fa-plus" />
      </a>
    </OverlayTrigger>
  );
};

AbsenceCounterCell.propTypes = {
  intl: intlShape.isRequired,
  counter: PropTypes.shape({
    id: PropTypes.string,
    balance: PropTypes.number,
    balanceWholeDays: PropTypes.number,
    balanceRemainderMinutes: PropTypes.number,
    startValue: PropTypes.number,
    startValueWholeDays: PropTypes.number,
    startValueRemainderMinutes: PropTypes.number,
  }),
  resourceId: PropTypes.string.isRequired,
  employeeId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  onInitCounter: PropTypes.func.isRequired,
  onDeleteCounter: PropTypes.func.isRequired,
  onOpenRegistrationOverview: PropTypes.func.isRequired,
  onOpenCounterDetails: PropTypes.func.isRequired,
  inDays: PropTypes.bool.isRequired,
};
AbsenceCounterCell.defaultProps = {
  counter: null,
};

export default AbsenceCounterCell;
